import { defineMutator } from "@rocicorp/zero"

import type { ZeroJwtPayload } from "@productlane/lib/src/browser"
import { MAX_VERSIONS_PER_ARTICLE } from "@productlane/lib/constants"
import {
	cascadedVisibility,
	needsCascade,
} from "@productlane/lib/src/article-visibility"
import { homeSectionLimit } from "@productlane/lib/src/help-center-home-sections"
import { normalizeDocsUrlName } from "@productlane/lib/src/portal-slugs"
import {
	nextVersionNumber,
	shouldMergeIntoHead,
} from "@productlane/lib/src/versions"

import type { MutatorTx, Schema } from "../schema"
import type {
	CreateHelpCenterArticleValue,
	CreateHelpCenterArticleVersionArgs,
	HelpCenterArticleHomeSectionArgs,
	HelpCenterArticleMove,
	HelpCenterArticlePortalMove,
	HelpCenterArticleReorder,
	HelpCenterArticlesReindex,
	HelpCenterHomeSectionClear,
	HelpCenterHomeSectionReorder,
} from "../types"
import type { DeleteID, JSONValue, UpdateValue } from "@rocicorp/zero"
import { keptCoverBlurhash } from "./changelogs"
import { supersedeProposalsForArticles } from "./help-center-proposal-status"

// A version row is unsafe to prune if any non-terminal proposal still anchors
// to it via baseVersionId. The DB enforces this with `ON DELETE RESTRICT`, so
// this check exists to avoid an attempted-then-rejected delete (which would
// crash the optimistic mutator and roll back the surrounding insert).
async function isVersionRowSafeToPrune(
	tx: MutatorTx,
	versionId: string,
): Promise<boolean> {
	const referencing = await tx.query.helpCenterArticleProposals
		.where("baseVersionId", "=", versionId)
		.run()
	return referencing.every((p) => p.status !== "OPEN" && p.status !== "DRAFT")
}

// A drag can leave a branch deeper than the tree views render, so the walk is
// bounded rather than trusting the shape.
const MAX_CASCADE_DEPTH = 10

// A subpage is only reachable through its parent, so hiding a parent hides the
// whole branch under it, not just its direct children. Without this a public
// grandchild stays live at its own URL and in search while the branch above it
// is gone from every customer surface.
export async function hidePublicSubpages(
	tx: MutatorTx,
	parentArticleId: string,
	parentVisibility: string,
): Promise<string[]> {
	const hiddenIds: string[] = []
	let level = [{ id: parentArticleId, visibility: parentVisibility }]

	for (let depth = 0; depth < MAX_CASCADE_DEPTH && level.length > 0; depth++) {
		const next: { id: string; visibility: string }[] = []

		for (const parent of level) {
			const subpages = await tx.query.helpCenterArticles
				.where("parentArticleId", "=", parent.id)
				.where("isDeleted", "=", false)
				.run()

			for (const subpage of subpages) {
				const effective = cascadedVisibility(
					parent.visibility,
					subpage.visibility,
				)
				next.push({ id: subpage.id, visibility: effective })
				if (needsCascade(parent.visibility, subpage.visibility)) {
					await tx.mutate.helpCenterArticles.update({
						id: subpage.id,
						visibility: effective,
					})
					hiddenIds.push(subpage.id)
				}
			}
		}

		level = next
	}

	return hiddenIds
}

export const helpCenterArticleMutators = {
	create: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: CreateHelpCenterArticleValue
			ctx: ZeroJwtPayload
		}) => {
			// A subpage is never more visible than its parent, whatever the caller
			// asks for: the branch is off that surface already.
			let visibility = args.visibility ?? "PUBLIC"
			if (args.parentArticleId) {
				const [parent] = await tx.query.helpCenterArticles
					.where("id", "=", args.parentArticleId)
					.run()
				if (parent) {
					visibility = cascadedVisibility(parent.visibility, visibility)
				}
			}

			await tx.mutate.helpCenterArticles.insert({
				id: args.id,
				title: args.title,
				helpCenterGroupId: args.helpCenterGroupId,
				parentArticleId: args.parentArticleId ?? null,
				order: args.order,
				createdAt: Date.now(),
				updatedAt: Date.now(),
				isDeleted: false,
				version: 1,
				workspaceId: authData.workspaceId,
				archived: false,
				content: {},
				summary: "",
				// A link has no draft state, so it lands live; docs start unpublished.
				published: args.kind === "LINK",
				visibility,
				kind: args.kind ?? "DOC",
				externalUrl: args.externalUrl ?? null,
				icon: args.icon || "Page2",
				urlName: args.id,
				showOnHomePage: true,
				// Multi-portal: callers pass the active portal id (or null for Main)
				// so the new article lands in the portal the user is currently
				// viewing. Without this, every new article would default to Main.
				portalInstanceId: args.portalInstanceId ?? null,
			})
		},
	),
	update: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: UpdateValue<Schema["tables"]["helpCenterArticles"]>
		}) => {
			// Never let an article parent itself or one of its own subpages: that
			// creates a cycle the nav query can't render, so the whole subtree
			// vanishes (it looks deleted but isn't). A drag bug did exactly this in
			// prod, so the invariant lives at the mutator, not just the UI.
			if (args.parentArticleId != null && args.parentArticleId === args.id) {
				throw new Error("An article cannot be its own parent")
			}
			if (args.parentArticleId != null && args.parentArticleId !== undefined) {
				const child = await tx.query.helpCenterArticles
					.where("parentArticleId", "=", args.id)
					.where("id", "=", args.parentArticleId)
					.run()
				if (child.length > 0) {
					throw new Error("An article cannot be nested under its own subpage")
				}
			}
			// An editor that never loaded the hash sends null with the same cover;
			// keep the stored one, or the cover loads without its blur placeholder.
			let row = args
			if (args.imageUrl !== undefined && args.imageBlurhash == null) {
				const [current] = await tx.query.helpCenterArticles
					.where("id", "=", args.id)
					.run()
				row = {
					...args,
					imageBlurhash: keptCoverBlurhash(current, args.imageUrl ?? null),
				}
			}
			await tx.mutate.helpCenterArticles.update(row)

			if (args.visibility && args.visibility !== "PUBLIC") {
				await hidePublicSubpages(tx, args.id, args.visibility)
			}
		},
	),
	moveToPortal: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: HelpCenterArticlePortalMove
		}) => {
			const subpages = await tx.query.helpCenterArticles
				.where("parentArticleId", "=", args.id)
				.run()

			await tx.mutate.helpCenterArticles.update({
				id: args.id,
				helpCenterGroupId: args.helpCenterGroupId,
				portalInstanceId: args.portalInstanceId,
				parentArticleId: null,
				updatedAt: Date.now(),
			})

			for (const subpage of subpages) {
				await tx.mutate.helpCenterArticles.update({
					id: subpage.id,
					helpCenterGroupId: args.helpCenterGroupId,
					portalInstanceId: args.portalInstanceId,
					updatedAt: Date.now(),
				})
			}
		},
	),
	// A deliberate slug edit. The server keeps the old path as a redirect; here
	// the synced rows are enough to reject a path another article already holds.
	setUrlName: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: { id: string; urlName: string }
			ctx: ZeroJwtPayload
		}) => {
			const normalized = normalizeDocsUrlName(args.urlName)
			if (!normalized.ok) throw new Error(normalized.message)

			const holders = await tx.query.helpCenterArticles
				.where("workspaceId", "=", authData.workspaceId)
				.where("urlName", "=", normalized.value)
				.where("isDeleted", "=", false)
				.run()
			const holder = holders.find((a) => a.id !== args.id)
			if (holder) {
				throw new Error(
					`"/docs/${normalized.value}" is used by "${holder.title || "Untitled"}"`,
				)
			}

			await tx.mutate.helpCenterArticles.update({
				id: args.id,
				urlName: normalized.value,
				updatedAt: Date.now(),
			})
		},
	),
	removeRedirect: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: { id: string; urlName: string }
			ctx: ZeroJwtPayload
		}) => {
			if (authData.role === "VIEWER") {
				throw new Error("Viewers cannot remove redirects")
			}
			const [article] = await tx.query.helpCenterArticles
				.where("id", "=", args.id)
				.where("workspaceId", "=", authData.workspaceId)
				.run()
			const previous = article?.previousUrlNames ?? []
			if (!previous.includes(args.urlName)) return

			await tx.mutate.helpCenterArticles.update({
				id: args.id,
				previousUrlNames: previous.filter((p) => p !== args.urlName),
			})
		},
	),
	delete: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: DeleteID<Schema["tables"]["helpCenterArticles"]>
			ctx: ZeroJwtPayload
		}) => {
			await tx.mutate.helpCenterArticles.update({
				id: args.id,
				isDeleted: true,
			})

			// Soft-delete subpages so they disappear from the UI alongside the parent.
			const subpages = await tx.query.helpCenterArticles
				.where("parentArticleId", "=", args.id)
				.where("isDeleted", "=", false)
				.run()
			for (const subpage of subpages) {
				await tx.mutate.helpCenterArticles.update({
					id: subpage.id,
					isDeleted: true,
				})
			}

			await supersedeProposalsForArticles(
				tx,
				[args.id, ...subpages.map((s) => s.id)],
				authData.userId,
				Date.now(),
			)
		},
	),
	// Drag-and-drop path. Deliberately writes nothing but position columns; the
	// server mutator hands urlName, search indexing and webhooks to Inngest.
	move: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: HelpCenterArticleMove }) => {
			if (args.parentArticleId === args.id) {
				throw new Error("An article cannot be its own parent")
			}
			if (args.parentArticleId != null) {
				const child = await tx.query.helpCenterArticles
					.where("parentArticleId", "=", args.id)
					.where("id", "=", args.parentArticleId)
					.run()
				if (child.length > 0) {
					throw new Error("An article cannot be nested under its own subpage")
				}
			}

			await tx.mutate.helpCenterArticles.update({
				id: args.id,
				helpCenterGroupId: args.helpCenterGroupId,
				parentArticleId: args.parentArticleId,
				order: args.order,
			})

			if (args.parentArticleId != null) {
				const [parent] = await tx.query.helpCenterArticles
					.where("id", "=", args.parentArticleId)
					.run()
				// Dropping a public article under a hidden parent hides it, the same
				// way flipping the parent hides the branch it already has.
				const [dragged] = await tx.query.helpCenterArticles
					.where("id", "=", args.id)
					.run()
				const draggedVisibility = dragged?.visibility ?? "PUBLIC"
				if (parent && needsCascade(parent.visibility, draggedVisibility)) {
					await tx.mutate.helpCenterArticles.update({
						id: args.id,
						visibility: cascadedVisibility(
							parent.visibility,
							draggedVisibility,
						),
					})
				}

				// The dragged article just became a subpage, so its own subpages would
				// sit a level below anything the app or portal nav renders: public,
				// reachable by URL, invisible in every tree. Lift them to the new
				// parent so they land as siblings of the article they followed.
				const children = await tx.query.helpCenterArticles
					.where("parentArticleId", "=", args.id)
					.where("isDeleted", "=", false)
					.run()
				if (children.length > 0) {
					const targetSiblings = await tx.query.helpCenterArticles
						.where("parentArticleId", "=", args.parentArticleId)
						.where("isDeleted", "=", false)
						.run()
					let order = Math.max(
						args.order,
						...targetSiblings.map((sibling) => sibling.order),
					)
					const ordered = [...children].sort((a, b) => a.order - b.order)
					for (const child of ordered) {
						order += 1
						await tx.mutate.helpCenterArticles.update({
							id: child.id,
							parentArticleId: args.parentArticleId,
							helpCenterGroupId: args.helpCenterGroupId,
							order,
							...(parent && needsCascade(parent.visibility, child.visibility)
								? {
										visibility: cascadedVisibility(
											parent.visibility,
											child.visibility,
										),
									}
								: {}),
						})
					}
				}
			}

			// Subpages follow their parent's group, otherwise they render as
			// orphans in the group the parent just left.
			for (const subpageId of args.subpageIds) {
				await tx.mutate.helpCenterArticles.update({
					id: subpageId,
					helpCenterGroupId: args.helpCenterGroupId,
				})
			}

			for (const sibling of args.siblings) {
				await tx.mutate.helpCenterArticles.update({
					id: sibling.id,
					order: sibling.order,
				})
			}
		},
	),
	// TODO: Use fractional indexing instead of numeric order for better concurrent reordering.
	// See: https://www.npmjs.com/package/fractional-indexing
	reorder: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: HelpCenterArticleReorder }) => {
			for (const item of args.items) {
				await tx.mutate.helpCenterArticles.update({
					id: item.id,
					order: item.order,
				})
			}
		},
	),
	reindex: defineMutator(
		async (_opts: { tx: MutatorTx; args: HelpCenterArticlesReindex }) => {
			// This is just a signal mutation - the real work happens on server side
			return Promise.resolve()
		},
	),
	setHomeSection: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: HelpCenterArticleHomeSectionArgs
			ctx: ZeroJwtPayload
		}) => {
			if (!args.section) {
				await tx.mutate.helpCenterArticles.update({
					id: args.id,
					homeSection: null,
					homeOrder: null,
				})
				return
			}

			const section = args.section
			const inSection = (
				await tx.query.helpCenterArticles
					.where("workspaceId", "=", authData.workspaceId)
					.where("homeSection", "=", section)
					.where("isDeleted", "=", false)
					.run()
			).filter((article) => article.id !== args.id)

			if (inSection.length >= homeSectionLimit(section)) {
				throw new Error(`The ${section} section is full`)
			}

			const lastOrder = inSection.reduce(
				(highest, article) => Math.max(highest, article.homeOrder ?? 0),
				-1,
			)

			await tx.mutate.helpCenterArticles.update({
				id: args.id,
				homeSection: section,
				homeOrder: lastOrder + 1,
				customHomeSectionId: null,
				showOnHomePage: true,
			})
		},
	),
	clearHomeSection: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: HelpCenterHomeSectionClear
			ctx: ZeroJwtPayload
		}) => {
			const inSection = await tx.query.helpCenterArticles
				.where("workspaceId", "=", authData.workspaceId)
				.where("homeSection", "=", args.section)
				.run()
			const ids = new Set(args.ids)
			for (const article of inSection) {
				if (!ids.has(article.id)) continue
				await tx.mutate.helpCenterArticles.update({
					id: article.id,
					homeSection: null,
					homeOrder: null,
				})
			}
		},
	),
	reorderHomeSection: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: HelpCenterHomeSectionReorder
		}) => {
			for (const [index, id] of args.ids.entries()) {
				await tx.mutate.helpCenterArticles.update({
					id,
					homeSection: args.section,
					homeOrder: index,
				})
			}
		},
	),
	createVersion: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: CreateHelpCenterArticleVersionArgs
			ctx: ZeroJwtPayload
		}) => {
			const versions = await tx.query.helpCenterArticleVersions
				.where("articleId", "=", args.articleId)
				.where("language", "=", args.language)
				.run()

			const source = args.createdBySource ?? "USER"
			const userId = source === "USER" ? authData.userId : null
			const now = Date.now()
			const head = [...versions].sort((a, b) => b.createdAt - a.createdAt)[0]

			// A run of saves by one author folds into the head row. Skipped while an
			// open proposal is anchored to it, since that row is its diff base.
			if (
				head &&
				shouldMergeIntoHead({
					head,
					source,
					userId,
					now,
					forceNewVersion: args.forceNewVersion,
				}) &&
				(await isVersionRowSafeToPrune(tx, head.id))
			) {
				// The step log stays valid by appending: head's steps carry
				// parent -> head, the incoming ones carry head -> new content.
				const mergedSteps =
					head.appliedSteps && args.appliedSteps
						? [
								...(head.appliedSteps as JSONValue[]),
								...(args.appliedSteps as JSONValue[]),
							]
						: null

				await tx.mutate.helpCenterArticleVersions.update({
					id: head.id,
					createdAt: now,
					title: args.title,
					content: args.content,
					imageUrl: args.imageUrl ?? null,
					imageBlurhash: args.imageBlurhash ?? null,
					summary: args.summary,
					icon: args.icon,
					appliedSteps: mergedSteps,
				})
				return
			}

			// Prune oldest version when over the cap, but skip rows that an
			// open proposal still references via baseVersionId - the FK is
			// `ON DELETE RESTRICT` and we'd hit the constraint otherwise.
			if (versions.length >= MAX_VERSIONS_PER_ARTICLE) {
				const sortedVersions = [...versions].sort(
					(a, b) => a.createdAt - b.createdAt,
				)
				for (const candidate of sortedVersions) {
					if (await isVersionRowSafeToPrune(tx, candidate.id)) {
						await tx.mutate.helpCenterArticleVersions.delete({
							id: candidate.id,
						})
						break
					}
				}
			}

			const [article] = await tx.query.helpCenterArticles
				.where("id", "=", args.articleId)
				.run()

			if (!article) {
				// Don't throw - this can happen due to sync timing and would crash the poke handler
				console.warn(
					`createVersion: Article ${args.articleId} not found, skipping version creation`,
				)
				return
			}

			// Resolve the parent (most recent) version of the same article+language
			// when the caller didn't pass one explicitly.
			const parentVersionId = args.parentVersionId ?? head?.id

			await tx.mutate.helpCenterArticleVersions.insert({
				id: args.id,
				articleId: args.articleId,
				createdAt: now,
				version: nextVersionNumber(versions),
				title: args.title,
				content: args.content,
				imageUrl: args.imageUrl ?? null,
				imageBlurhash: args.imageBlurhash ?? null,
				summary: args.summary,
				icon: args.icon,
				language: args.language,
				appliedSteps: args.appliedSteps ?? null,
				parentVersionId: parentVersionId ?? null,
				createdBySource: source,
				createdById: userId,
			})
		},
	),
	deleteVersionsByLanguage: defineMutator(
		async ({
			tx,
			args: { workspaceId, language },
		}: {
			tx: MutatorTx
			args: { workspaceId: string; language: string }
		}) => {
			// Get all articles for this workspace
			const articles = await tx.query.helpCenterArticles
				.where("workspaceId", "=", workspaceId)
				.related("versions", (q) => q.where("language", "=", language))
				.run()

			// For each article, delete all versions with the specified language
			await Promise.all(
				articles.map((article) =>
					Promise.all(
						article.versions.map((version) =>
							tx.mutate.helpCenterArticleVersions.delete({ id: version.id }),
						),
					),
				),
			)
		},
	),
	duplicate: defineMutator(
		async ({
			tx,
			args: { id, duplicateId },
			ctx: authData,
		}: {
			tx: MutatorTx
			args: { id: string; duplicateId: string }
			ctx: ZeroJwtPayload
		}) => {
			// Get the original article
			const [originalArticle] = await tx.query.helpCenterArticles
				.where("id", "=", id)
				.where("workspaceId", "=", authData.workspaceId)
				.run()

			if (!originalArticle) {
				throw new Error(`Article with id ${id} not found`)
			}

			// Get all articles in the same group to find max order
			let groupArticles: (typeof originalArticle)[] = []
			if (originalArticle.helpCenterGroupId) {
				groupArticles = await tx.query.helpCenterArticles
					.where("helpCenterGroupId", "=", originalArticle.helpCenterGroupId)
					.where("isDeleted", "=", false)
					.run()
			} else {
				throw new Error("Article is not associated with a help center group")
			}

			const maxOrder =
				groupArticles.length > 0
					? Math.max(...groupArticles.map((a) => a.order))
					: originalArticle.order

			// Create duplicate article
			await tx.mutate.helpCenterArticles.insert({
				id: duplicateId,
				title: `${originalArticle.title} Copy`,
				helpCenterGroupId: originalArticle.helpCenterGroupId,
				parentArticleId: originalArticle.parentArticleId,
				order: maxOrder + 1,
				createdAt: Date.now(),
				updatedAt: Date.now(),
				isDeleted: false,
				version: 1,
				workspaceId: authData.workspaceId,
				archived: false,
				content: originalArticle.content,
				summary: originalArticle.summary,
				published: false,
				visibility: originalArticle.visibility,
				kind: originalArticle.kind,
				externalUrl: originalArticle.externalUrl,
				icon: originalArticle.icon,
				urlName: duplicateId, // Will be properly updated by serverside mutator
				showOnHomePage: false,
				imageUrl: originalArticle.imageUrl,
				imageBlurhash: originalArticle.imageBlurhash,
				date: originalArticle.date,
				// Keep the duplicate in the same portal as the source.
				portalInstanceId: originalArticle.portalInstanceId,
			})
		},
	),
}
